import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LogOut,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import withRouter from "Common/withRouter";
import MenuData from "Layout/LayoutMenuData";
import { getUsername } from "Common/Components/getRoleId";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  link?: string;
  path?: string;
  subItems?: MenuItem[];
}

interface SidebarProps {
  layoutType?: string;
  layoutSidebarSizeType?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  layoutType = "vertical",
  layoutSidebarSizeType = "lg",
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["users", "settings", "support"]);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const [username, setusername] = useState<any>(null);
  const token = localStorage.getItem("app_havir")
  useEffect(() => {
    setusername(getUsername(token));
  }, [token]);

  const menuItems: MenuItem[] = MenuData();

  const isActive = (path?: string) => {
    if (!path) return false;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const currentPath = location.pathname;
    if (currentPath === cleanPath) return true;
    if (cleanPath === '/#' && currentPath === '/') return true;
    return false;
  };

  const isSubMenuActive = (subItems?: MenuItem[]) => {
    if (!subItems) return false;
    return subItems.some(item => {
      const itemPath = item.link || item.path;
      return isActive(itemPath);
    });
  };

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const closeMobileSidebar = () => {
    if (window.innerWidth < 1024) setIsMobileOpen(false);
  };

  const isCollapsed = layoutSidebarSizeType === "sm";
  const showFull = !isCollapsed || (isCollapsed && isHovered);
  const currentWidth = isCollapsed ? (isHovered ? "260px" : "80px") : "260px";

  const renderMenuItem = (item: MenuItem, showFull: boolean) => {
    const hasSubItems = !!(item.subItems && item.subItems.length);
    const isExpanded = expandedMenus.includes(item.id);

    // تعیین استایل برای آیتم‌های دارای زیرمجموعه (والد) – همیشه معمولی، هرگز آبی نمی‌شوند
    const parentClasses = "text-gray-700 hover:bg-blue-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-400";

    // استایل برای آیتم‌های بدون زیرمجموعه (برگ) – در صورت active بودن آبی می‌شوند
    const isLeafActive = isActive(item.link || item.path);
    const leafActiveClasses = isLeafActive
      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/25"
      : "text-gray-700 hover:bg-blue-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-400";

    if (hasSubItems) {
      return (
        <li key={item.id} className="mb-1">
          <button
            onClick={() => toggleMenu(item.id)}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-200 group ${parentClasses}`}
          >
            <div className="flex items-center gap-3">
              <span className="transition-transform duration-200 group-hover:scale-110">
                {item.icon}
              </span>
              {showFull && <span className="text-sm font-medium">{item.label}</span>}
            </div>
            {showFull && (
              <ChevronRight
                size={16}
                className={`transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            )}
          </button>
          {showFull && (
            <div
              className="overflow-hidden transition-all duration-200 ease-out"
              style={{
                maxHeight: isExpanded ? "500px" : "0",
                transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <ul className="mr-6 mt-1 space-y-1 border-r-2 border-blue-200 dark:border-blue-800">
                {item.subItems!.map(subItem => renderMenuItem(subItem, showFull))}
              </ul>
            </div>
          )}
        </li>
      );
    }

    return (
      <li key={item.id} className="mb-1">
        <Link
          to={item.link || item.path || "#"}
          onClick={closeMobileSidebar}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group ${leafActiveClasses}`}
        >
          <span className="transition-transform duration-200 group-hover:scale-110">
            {item.icon}
          </span>
          {showFull && <span className="text-sm font-medium">{item.label}</span>}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* دکمه باز کردن سایدبار در موبایل */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-2.5 right-6 z-[1001] w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center border border-gray-200 dark:bg-gray-800/80 dark:border-gray-700"
      >
        <Menu size={24} className="text-gray-700 dark:text-gray-200" />
      </button>

      {/* سایدبار اصلی */}
      <div
        className={`app-menu sidebar-desktop fixed top-0 right-0 h-screen z-[1003] flex flex-col overflow-hidden transition-all duration-200 ease-in-out
          bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800
          border-l border-blue-100/50 dark:border-blue-900/30 shadow-2xl dark:shadow-gray-900/30`}
        style={{ width: currentWidth }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* هدر لوگو */}
        <div className="relative px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-center">
          <Link to="/dashboard" className="block">
            {!showFull ? (
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">هـ</span>
              </div>
            ) : (
              <>
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg mb-3">
                  <span className="text-white font-bold text-3xl">هـ</span>
                </div>
                <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  پایش هوشمند آرد
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">نسخه ۲.۰</p>
              </>
            )}
          </Link>
          <button
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden absolute left-3 top-5 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X size={20} />
          </button>
        </div>

        {/* پروفایل کاربر */}
        {showFull && (
          <div className="mx-4 mt-3 p-2 bg-blue-50/80 dark:bg-blue-900/20 rounded-xl flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow">
              ا
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-white">خوش آمدید</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400"> {username}</p>
            </div>
          </div>
        )}

        {/* منوها */}
        <div className="flex-1 overflow-y-auto py-3 px-3 custom-scrollbar">
          <ul className="space-y-1">
            {menuItems.map(item => renderMenuItem(item, showFull))}
          </ul>
        </div>

        {/* دکمه خروج */}
        {showFull && (
          <div className="p-2 border-t border-gray-200 dark:border-gray-700 mt-auto">
            <button
              onClick={() => {
                localStorage.removeItem("app_havir");
                localStorage.removeItem("user_role");
                window.location.href = "/login";
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400 transition-all duration-200 group"
            >
              <LogOut size={20} className="transition-transform group-hover:scale-110" />
              <span className="text-sm font-medium">خروج از سیستم</span>
            </button>
          </div>
        )}
      </div>

      {/* اوورلی موبایل */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[1002] lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* استایل اسکرول */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #06b6d4);
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #1f2937;
        }
        @media (max-width: 1023px) {
          .sidebar-desktop {
            transform: translateX(${isMobileOpen ? "0" : "100%"});
            transition: transform 0.3s ease-in-out;
          }
        }
      `}</style>
    </>
  );
};

export default withRouter(Sidebar);